import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const DEFAULTS = {
  about: {
    cta_label: "Read the Writings",
  },
  writings: {
    kicker: "The Collection",
    heading: "Writings & _words_",
    description:
      "Children's stories, poetry, and short articles — gathered here to encourage you in whatever season finds you today.",
    empty_message: "No writings here yet — come back soon.",
  },
  listen: {
    kicker: "Listen",
    heading: "Poetry, set to music.",
    description:
      "A handful of my poems — given voice and melody through Suno. Press play, settle in, and let the words sing.",
    empty_message:
      "The first song is on its way. Check back soon for poems set to music.",
  },
};

const FIELD = ({ section, k, label, helper, type = "text", serif = false, form, set, reset }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft">{label}</label>
      <button
        type="button"
        onClick={() => reset(section, k)}
        data-testid={`admin-pages-reset-${section}-${k}`}
        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta"
      >
        <RotateCcw size={12} /> Reset
      </button>
    </div>
    {type === "textarea" ? (
      <textarea
        rows={3}
        value={form[section][k] || ""}
        onChange={(e) => set(section, k, e.target.value)}
        data-testid={`admin-pages-${section}-${k}`}
        className={`w-full bg-paper border border-rule px-4 py-3 text-ink leading-relaxed outline-none focus:border-ink ${serif ? "font-serif-display text-xl" : "text-base"}`}
      />
    ) : (
      <input
        value={form[section][k] || ""}
        onChange={(e) => set(section, k, e.target.value)}
        data-testid={`admin-pages-${section}-${k}`}
        className={`w-full bg-transparent border-b border-rule focus:border-ink py-3 outline-none text-ink ${serif ? "font-serif-display text-2xl" : "text-sm"}`}
      />
    )}
    {helper && <p className="mt-2 text-xs text-ink-soft italic leading-relaxed">{helper}</p>}
  </div>
);

export default function AdminPages() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/pages")
      .then((r) =>
        setForm({
          about: { ...DEFAULTS.about, ...(r.data?.about || {}) },
          writings: { ...DEFAULTS.writings, ...(r.data?.writings || {}) },
          listen: { ...DEFAULTS.listen, ...(r.data?.listen || {}) },
        }),
      )
      .finally(() => setLoading(false));
  }, []);

  const set = (section, k, v) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [k]: v } }));
  const reset = (section, k) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [k]: DEFAULTS[section][k] } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pages", form);
      toast.success("Pages saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="admin-pages-loading">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-pages-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-pages-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
          Site Copy
        </p>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
          About & Writings pages
        </h1>
        <p className="mt-3 text-sm text-ink-soft max-w-2xl">
          Tip: use <code className="text-ink">*highlight*</code> for terracotta, <code className="text-ink">_italic_</code> for italic. (Your bio, name, photo, and quote are edited on the <Link to="/admin/profile" className="text-terracotta link-underline">Profile</Link> page.)
        </p>

        <div className="mt-14 border-b border-rule pb-4 mb-8">
          <p className="text-xs uppercase tracking-[0.22em] text-sage">About Page</p>
        </div>
        <div className="space-y-8">
          <FIELD section="about" k="cta_label" label="Button Label" form={form} set={set} reset={reset} />
        </div>

        <div className="mt-16 border-b border-rule pb-4 mb-8">
          <p className="text-xs uppercase tracking-[0.22em] text-sage">Writings Page</p>
        </div>
        <div className="space-y-8">
          <FIELD section="writings" k="kicker" label="Small Kicker" form={form} set={set} reset={reset} />
          <FIELD section="writings" k="heading" label="Heading" serif form={form} set={set} reset={reset} helper="*highlight* and _italic_ supported." />
          <FIELD section="writings" k="description" label="Description" type="textarea" form={form} set={set} reset={reset} />
          <FIELD section="writings" k="empty_message" label="Empty-State Message" type="textarea" form={form} set={set} reset={reset} />
        </div>

        <div className="mt-16 border-b border-rule pb-4 mb-8">
          <p className="text-xs uppercase tracking-[0.22em] text-sage">Listen Page</p>
        </div>
        <div className="space-y-8">
          <FIELD section="listen" k="kicker" label="Small Kicker" form={form} set={set} reset={reset} />
          <FIELD section="listen" k="heading" label="Heading" serif form={form} set={set} reset={reset} helper="*highlight* and _italic_ supported." />
          <FIELD section="listen" k="description" label="Intro / Description" type="textarea" form={form} set={set} reset={reset} />
          <FIELD section="listen" k="empty_message" label="Message when no songs are featured" type="textarea" form={form} set={set} reset={reset} />
        </div>

        <div className="mt-12 pt-6 border-t border-rule flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-pages-save-btn"
            className="px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Pages"}
          </button>
        </div>
      </div>
    </div>
  );
}
